import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  gradientTextStyles,
  Page,
  PrimaryButton,
  theme,
  typography,
} from "../Homepage/styles";

/** Override global a:hover underline (shards-dashboards) on card-style links. */
const cardLinkNoUnderline = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover *,
  &:focus *,
  &:active *,
  &:visited * {
    text-decoration: none;
  }
`;

export const PublicBlogPage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;

export const PublicBlogHero = styled.section`
  position: relative;
  padding: 104px 16px 40px;
  overflow: hidden;
  text-align: center;

  @media (min-width: 768px) {
    padding: 128px 24px 48px;
  }
`;

export const PublicBlogHeroBgGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    hsl(var(--hp-primary) / 0.1) 0%,
    hsl(var(--hp-background)) 50%,
    hsl(var(--hp-accent) / 0.05) 100%
  );
`;

export const PublicBlogHeroBgRadial = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    hsl(var(--hp-primary) / 0.15) 0%,
    transparent 70%
  );
`;

export const PublicBlogHeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 768px;
  margin: 0 auto;
`;

export const PublicBlogHeroTitle = styled.h1`
  margin: 0 0 24px;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 3.75rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const PublicBlogHeroLead = styled.p`
  margin: 0;
  color: ${theme.textMuted};
  font-size: 1.125rem;
  line-height: 1.6;
`;

export const PublicBlogFeedSection = styled.section`
  padding: 0 16px 80px;
`;

export const PublicBlogContentInner = styled.div`
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const PublicBlogEmptyState = styled.div`
  ${glassCardStyles}
  padding: 48px 24px;
  border-radius: 16px;
  text-align: center;
  color: ${theme.textMuted};
  font-size: 0.9375rem;
`;

export const PublicBlogPaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

export const PublicBlogCardRoot = styled.article`
  position: relative;
  ${glassCardStyles}
  overflow: hidden;
  border-radius: 16px;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.4);
  }
`;

export const PublicBlogCardOverlayLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 16px;
  ${cardLinkNoUnderline}

  span {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

export const PublicBlogCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  min-width: 0;

  @media (min-width: 640px) {
    grid-template-columns: 220px minmax(0, 1fr);
    align-items: stretch;
  }
`;

export const PublicBlogCardMedia = styled.div`
  position: relative;
  height: 160px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    hsl(var(--hp-primary) / 0.2) 0%,
    ${theme.bg} 50%,
    hsl(var(--hp-accent) / 0.1) 100%
  );

  @media (min-width: 640px) {
    height: 100%;
    min-height: 176px;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${PublicBlogCardRoot}:hover & img {
    transform: scale(1.05);
  }
`;

export const PublicBlogCardMediaGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top right,
    hsl(var(--hp-background) / 0.7) 0%,
    hsl(var(--hp-background) / 0.1) 50%,
    transparent 100%
  );
`;

export const PublicBlogTypePill = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: hsl(var(--hp-background) / 0.8);
  backdrop-filter: blur(8px);
  color: ${theme.foreground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};

  .material-icons {
    font-size: 14px;
    color: ${theme.primary};
  }
`;

export const PublicBlogCardBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px;

  @media (min-width: 640px) {
    padding: 24px;
  }
`;

export const PublicBlogAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  min-width: 0;

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.3);
  }

  strong {
    display: block;
    color: ${theme.foreground};
    font-size: 0.875rem;
    font-weight: ${typography.weights.semibold};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    color: ${theme.textMuted};
    font-size: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const PublicBlogAuthorLink = styled(Link)`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  ${cardLinkNoUnderline}

  &:hover strong {
    color: ${theme.primary};
  }
`;

export const PublicBlogCardTitle = styled.h2`
  margin: 0 0 8px;
  color: ${theme.foreground};
  font-size: 1.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.35;
  transition: color 0.2s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }

  ${PublicBlogCardRoot}:hover & {
    color: ${theme.primary};
  }
`;

export const PublicBlogCardExcerpt = styled.p`
  margin: 0 0 16px;
  color: ${theme.textMuted};
  font-size: 0.9375rem;
  line-height: 1.65;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const PublicBlogTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

export const PublicBlogTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-secondary) / 0.6);
  color: ${theme.textMuted};
  font-size: 0.6875rem;
`;

export const PublicBlogMeta = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid hsl(var(--hp-border) / 0.4);
  color: ${theme.textMuted};
  font-size: 0.75rem;
`;

export const PublicBlogMetaDate = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .material-icons {
    font-size: 14px;
  }
`;

export const FeaturedContributorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.3;
  white-space: nowrap;
`;

export const PublicBlogCta = styled.section`
  position: relative;
  padding: 80px 16px;
  overflow: hidden;
  text-align: center;
`;

export const PublicBlogCtaBg = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    hsl(var(--hp-primary) / 0.1) 0%,
    hsl(var(--hp-background)) 50%,
    hsl(var(--hp-accent) / 0.05) 100%
  );
`;

export const PublicBlogCtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 768px;
  margin: 0 auto;
`;

export const PublicBlogCtaTitle = styled.h2`
  margin: 0 0 24px;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const PublicBlogCtaLead = styled.p`
  margin: 0 0 32px;
  color: ${theme.textMuted};
  font-size: 1.125rem;
  line-height: 1.6;
`;

export const PublicBlogCtaButton = styled(PrimaryButton)`
  min-height: 56px;
  padding: 0 36px;
  font-size: 1rem;
`;

export const SkeletonCard = styled.div`
  height: 200px;
  border-radius: 16px;
  background: hsl(var(--hp-muted) / 0.5);
  animation: public-blog-pulse 1.5s ease-in-out infinite;

  @keyframes public-blog-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
